import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import PageHeader from "../components/PageHeader";
import addIcon from "../assets/plus.svg";
import searchicon from "../assets/search-icon.svg";
import gallary from "../assets/Gallery-Add.svg";
import uploadIcon from "../assets/upload-icon1.svg";
import infoCircle from "../assets/info-circle.svg";
import arrowdown from "../assets/sm-arrow-down.svg";
import minusIcon from "../assets/minus-circle.svg";
import editIcon from "../assets/pen1.svg";
import fbIcon from "../assets/fb.svg";
import ytIcon from "../assets/yt.svg";
import inIcon from "../assets/in.svg";
import instaIcon from "../assets/insta.svg";
import { requestWithToken } from "../utils/apis";
import { Spinner } from "react-bootstrap";
import Help from "../components/Help";
import { bucketUrl, helpPagesData } from "../utils/helper";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  DndContext,
  pointerWithin,
  useDroppable,
  DragOverlay,
  useSensor,
  useSensors,
  PointerSensor,
  rectIntersection,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import CommonModal from "../components/CommonModal";
import DeployTrigger from "../components/DeployTrigger";
import { toast } from "react-toastify";
import DataSortableTree from "../components/DNDSortableTree";
import MediaPickerModal from "../components/MediaPickerModal";
import NavigationRegionSpecific from "../components/NavigationRegionSpecific";
import SwitchToggle from "../components/switch";

const isValidMultiLangObject = (value) => {
  if (!value || typeof value !== "object") return false;

  const values = Object.values(value);

  if (values.length === 0) return false;

  return values.every(
    (val) => val !== null && val !== undefined && String(val).trim() !== "",
  );
};

const isValidStringOrMultiLang = (value) => {
  // Allow string
  if (typeof value === "string") {
    return value.trim() !== "";
  }

  // Allow object (multi-lang)
  if (value && typeof value === "object") {
    const values = Object.values(value);

    if (!values.length) return false;

    return values.every(
      (val) => val !== null && val !== undefined && String(val).trim() !== "",
    );
  }

  return false;
};

const isValidUrlOrMultiLang = (value) => {
  if (value === undefined || value === null) return false;
  if (typeof value === "string") return true;
  if (typeof value === "object") {
    const values = Object.values(value);
    if (!values.length) return true;
    return values.every((val) => val !== null && val !== undefined);
  }
  return false;
};

const isBooleanOrObject = (value) => {
  if (typeof value === "boolean") return true;
  if (value && typeof value === "object") return true;
  return false;
};

const menuItemSchema = Yup.object().shape({
  _id: Yup.string().notRequired(),
  page_id: Yup.string().notRequired(),
  menu_id: Yup.string().notRequired(),

  name: Yup.mixed().test(
    "name-required",
    "Name is required (string or multi-lang object)",
    isValidStringOrMultiLang,
  ),

  redirect_to: Yup.mixed().test(
    "url-required",
    "URL is required (string or multi-lang object)",
    isValidUrlOrMultiLang,
  ),

  children: Yup.array()
    .of(Yup.lazy(() => menuItemSchema))
    .notRequired(),

  extras: Yup.mixed().test(
    "extras-valid",
    "Extras must be string or object",
    (value) => {
      if (!value) return true;

      // string allowed
      if (typeof value === "string") return true;

      // object allowed (no strict validation unless you want)
      if (typeof value === "object") return true;

      return false;
    },
  ),

  menu_type: Yup.string().notRequired(),
  siblings: Yup.array().optional(),
  type: Yup.string().notRequired(),
  target_blank: Yup.mixed().test(
    "target-blank-valid",
    "target_blank must be boolean or object",
    isBooleanOrObject,
  ),
});
const validationSchema = Yup.array().of(menuItemSchema);

// 🔴 Get first error from deeply nested object
const getFirstError = (errObj) => {
  if (typeof errObj === "string") return errObj;

  if (Array.isArray(errObj)) {
    for (let e of errObj) {
      const msg = getFirstError(e);
      if (msg) return msg;
    }
  }

  if (typeof errObj === "object" && errObj !== null) {
    for (let key in errObj) {
      const msg = getFirstError(errObj[key]);
      if (msg) return msg;
    }
  }

  return null;
};

/* ---------------- Custom collision detection ---------------- */
const customCollisionDetection = (args) => {
  const pointerCollisions = pointerWithin(args);

  if (pointerCollisions.length > 0) {
    return pointerCollisions;
  }

  return rectIntersection(args);
};

/* ---------------- Improved Droppables ---------------- */
function DroppableAllPages({ children, isOver, scrollRef }) {
  const { setNodeRef } = useDroppable({
    id: "ALL_PAGES",
    data: {
      type: "container",
      accepts: ["page"],
    },
  });

  const combinedRef = useCallback(
    (node) => {
      setNodeRef(node);
      if (scrollRef) {
        scrollRef.current = node;
      }
    },
    [setNodeRef, scrollRef],
  );

  return <div ref={combinedRef}>{children}</div>;
}

function DroppableMenuPages({ children, isOver, scrollRef, hasItems }) {
  const { setNodeRef } = useDroppable({
    id: "MENU_PAGES",
    data: {
      type: "container",
      accepts: ["menu", "page"],
    },
  });

  const combinedRef = useCallback(
    (node) => {
      setNodeRef(node);
      if (scrollRef) {
        scrollRef.current = node;
      }
    },
    [setNodeRef, scrollRef],
  );

  return (
    <div ref={combinedRef} className="cu-899" data-menu-pages-root="true">
      {children}
    </div>
  );
}

function DroppableMenuItem({ id, children, isOver }) {
  const { setNodeRef } = useDroppable({
    id: `droppable-menu-${id}`,
    data: {
      type: "container",
      accepts: ["menu", "page"],
    },
  });

  return (
    <div
      ref={setNodeRef}
      data-droppable={`droppable-${id}`}
      style={{
        backgroundColor: isOver ? "#eef6ff" : undefined,
        border: isOver ? "2px dashed #2563eb" : "none",
        borderRadius: "6px",
        padding: isOver ? "12px" : "0",
        transition: "all 0.15s ease",
        minHeight: isOver ? "70px" : "auto",
        position: "relative",
      }}
    >
      {isOver && (
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: "25%",
            pointerEvents: "none",
            borderRight: "1px dashed rgba(0,0,0,0.06)",
          }}
        />
      )}
      {children}
    </div>
  );
}

/* ---------------- Sortable Components ---------------- */
function SortableMenuItem({
  item,
  children,
  onEdit,
  onRemove,
  isExpanded,
  onToggleExpand,
  hasChildren,
  isOver,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `menu-${item.__uid}`,
    data: {
      type: "item",
      item: item,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || "transform 200ms ease",
    opacity: isDragging ? 0.4 : 1,
    cursor: isDragging ? "grabbing" : "grab",
    userSelect: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px",
    backgroundColor: isDragging ? "#f3f4f6" : "white",
    border: "1px solid #e5e7eb",
    borderRadius: "6px",
    boxShadow: isDragging ? "0 4px 8px rgba(0, 0, 0, 0.1)" : "none",
  };

  return (
    <div style={{ position: "relative" }}>
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        className="page-menu-item d-flex align-items-center"
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {hasChildren ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand(item.__uid);
              }}
              aria-label={isExpanded ? "Collapse" : "Expand"}
              className="collapse-toggle"
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                padding: 4,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                style={{ transform: isExpanded ? "rotate(90deg)" : "none" }}
              >
                <path
                  d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z"
                  fill="currentColor"
                />
              </svg>
            </button>
          ) : (
            <div style={{ width: 24 }} />
          )}

          <span style={{ fontWeight: 500 }}>{item.name}</span>
          {!item.page_id && (
            <span style={{ color: "#888", fontSize: 10 }}> [link]</span>
          )}
        </div>

        <div className="d-flex align-items-center"></div>
      </div>

      {!isDragging && !isOver && children}
    </div>
  );
}

function DraggablePageItem({ page }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `page-${page._id || page.id}`,
    data: {
      type: "page",
      page: page,
    },
  });

  const style = {
    // transform: CSS.Transform.toString(transform),
    transition: transition || "transform 200ms ease",
    opacity: isDragging ? 0.4 : 1,
    cursor: isDragging ? "grabbing" : "grab",
    userSelect: "none",
    padding: "12px 16px",
    // backgroundColor: isDragging ? "#f3f4f6" : "white",
    border: "1px solid #e5e7eb",
    borderRadius: "6px",
    marginBottom: "8px",
    // boxShadow: isDragging ? "0 4px 8px rgba(0, 0, 0, 0.1)" : "none",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="page-menu-item"
    >
      {page.name}
    </div>
  );
}

/* ---------------- SortableMenuTree Component ---------------- */
function SortableMenuTree({
  items = [],
  overId,
  onEdit,
  onRemove,
  expandedUids,
  toggleExpand,
}) {
  const ids = items.map((i) => `menu-${i.__uid}`);

  return (
    <SortableContext items={ids} strategy={verticalListSortingStrategy}>
      {items.map((item) => {
        const isOver = overId === `droppable-menu-${item.__uid}`;
        const isExpanded = expandedUids.has(item.__uid);
        const hasChildren = item.children && item.children.length > 0;

        return (
          <div key={item.__uid}>
            <DroppableMenuItem id={item.__uid} isOver={isOver}>
              <SortableMenuItem
                item={item}
                onEdit={onEdit}
                onRemove={onRemove}
                isExpanded={isExpanded}
                onToggleExpand={toggleExpand}
                hasChildren={hasChildren}
                isOver={isOver}
              />

              {hasChildren && isExpanded && (
                <div
                  className="submenu-items"
                  style={{
                    marginLeft: "28px",
                    borderLeft: "2px solid #e5e7eb",
                    paddingLeft: "20px",
                  }}
                >
                  <SortableMenuTree
                    items={item.children}
                    overId={overId}
                    onEdit={onEdit}
                    onRemove={onRemove}
                    expandedUids={expandedUids}
                    toggleExpand={toggleExpand}
                  />
                </div>
              )}
            </DroppableMenuItem>
          </div>
        );
      })}
    </SortableContext>
  );
}

/* ---------------- Stable UID helpers ---------------- */
const makeUID = () =>
  `uid_${Date.now().toString(36)}_${Math.floor(Math.random() * 10000)}`;

function ensureUIDs(menu = []) {
  return (menu || []).map((item) => {
    const copy = { ...item };
    if (!copy.__uid) copy.__uid = makeUID();
    if (Array.isArray(copy.children) && copy.children.length) {
      copy.children = ensureUIDs(copy.children);
    } else {
      copy.children = copy.children || [];
    }
    return copy;
  });
}

/* ---------------- Simple Tree Operations ---------------- */
function collectAllMenuPageIds(menuItems = []) {
  let ids = [];
  (menuItems || []).forEach((item) => {
    if (item.page_id) ids.push(item.page_id);
    if (item.children?.length)
      ids = ids.concat(collectAllMenuPageIds(item.children));
  });
  return ids;
}

function addMenuType(menuArr, menuType) {
  return (menuArr || []).map((item) => ({
    ...item,
    menu_type: menuType,
    children: item.children ? addMenuType(item.children, menuType) : [],
  }));
}

// Deep clone function to prevent reference issues
const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

/**
 * findItemWithContext
 * returns { item, parent, parentArray, index } or null
 */
const findItemWithContext = (
  items,
  uid,
  parent = null,
  parentArray = null,
  index = -1,
) => {
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.__uid === uid) {
      return { item, parent, parentArray: parentArray || items, index: i };
    }
    if (item.children?.length) {
      const found = findItemWithContext(
        item.children,
        uid,
        item,
        item.children,
        i,
      );
      if (found) return found;
    }
  }
  return null;
};

const findUidByAnyId = (items, targetId) => {
  if (targetId == null) return null;
  const target = String(targetId);
  for (const item of items || []) {
    const candidateIds = [item.__uid, item._id, item.page_id, item.id]
      .filter(Boolean)
      .map(String);
    if (candidateIds.includes(target)) {
      return item.__uid;
    }
    if (item.children?.length) {
      const childUid = findUidByAnyId(item.children, target);
      if (childUid) return childUid;
    }
  }
  return null;
};

const findDepthByUid = (items, uid, depth = 0) => {
  for (const item of items || []) {
    if (item.__uid === uid) return depth;
    if (item.children?.length) {
      const childDepth = findDepthByUid(item.children, uid, depth + 1);
      if (childDepth !== null) return childDepth;
    }
  }
  return null;
};

const resolveDropModeByPointerX = (rect, pointerX, previousMode = "before") => {
  if (!rect) return "before";
  const threshold = rect.width * 0.35;
  const hysteresis = 10;
  const relativeX = pointerX - rect.left;

  if (previousMode === "child") {
    return relativeX > threshold - hysteresis ? "child" : "before";
  }

  return relativeX > threshold + hysteresis ? "child" : "before";
};

/**
 * removeItemByUid
 * Purely returns a new tree *without* the item having given uid.
 */
const removeItemByUid = (items, uid) => {
  const result = [];
  for (const item of items) {
    if (item.__uid === uid) {
      continue;
    }
    if (item.children && item.children.length) {
      result.push({
        ...item,
        children: removeItemByUid(item.children, uid),
      });
    } else {
      result.push({ ...item });
    }
  }
  return result;
};

/**
 * insertBeforeUid
 * Inserts newItem before the element with targetUid somewhere in the tree.
 * Returns new tree (no mutation).
 */
const insertBeforeUid = (items, targetUid, newItem) => {
  const result = [];
  let inserted = false;

  for (const item of items) {
    if (item.__uid === targetUid) {
      result.push({ ...newItem });
      result.push({ ...item, children: item.children ? item.children : [] });
      inserted = true;
      continue;
    }

    if (item.children && item.children.length) {
      const updatedChildren = insertBeforeUid(
        item.children,
        targetUid,
        newItem,
      );
      const childHasInserted = updatedChildren.some(
        (c) =>
          c.__uid === newItem.__uid ||
          (c.children && c.children.some((cc) => cc.__uid === newItem.__uid)),
      );

      if (childHasInserted) {
        result.push({ ...item, children: updatedChildren });
        inserted = true;
      } else {
        result.push({
          ...item,
          children: item.children ? item.children.map((c) => ({ ...c })) : [],
        });
      }
    } else {
      result.push({ ...item });
    }
  }

  if (!inserted) {
    return items.map((it) => ({
      ...it,
      children: it.children ? it.children.map((c) => ({ ...c })) : [],
    }));
  }

  return result;
};

/**
 * addItemAsChild
 * Adds newItem as last child of parentUid (searches recursively) and returns new tree.
 */
const addItemAsChild = (items, parentUid, newItem) => {
  return items.map((item) => {
    if (item.__uid === parentUid) {
      return {
        ...item,
        children: [
          ...(item.children || []).map((c) => ({ ...c })),
          { ...newItem },
        ],
      };
    }
    if (item.children && item.children.length) {
      return {
        ...item,
        children: addItemAsChild(item.children, parentUid, newItem),
      };
    }
    return { ...item };
  });
};

const insertAtRootIndex = (items, index, newItem) => {
  const safeIndex = Math.max(0, Math.min(index, items.length));
  const rootItems = items.map((item) => ({ ...item }));
  rootItems.splice(safeIndex, 0, { ...newItem });
  return rootItems;
};

// Function to remove __uid from menu items recursively
const removeUidsFromMenu = (menuItems = []) => {
  return menuItems.map(({ __uid, children, ...item }) => ({
    ...item,
    children: children ? removeUidsFromMenu(children) : [],
  }));
};

/* ---------------- Navigation component ---------------- */
export default function Navigation() {
  const [selectedMenuType, setSelectedMenuType] = useState(null);
  const [editMenuItem, setEditMenuItem] = useState({ item: null, path: [] });
  const [menuTypes, setMenuTypes] = useState({ loading: true, data: [] });
  const [pageList, setPageList] = useState({ loading: true, data: [] });
  const [selectedMenuData, setSelectedMenuData] = useState({
    loading: false,
    data: null,
  });
  const [activeModal, setActiveModal] = useState(null);
  const [menuState, setMenuState] = useState([]);
  const [openSection, setOpenSection] = useState(null);
  const [navIcon, setNavIcon] = useState("");
  const [openInNewTab, setOpenInNewTab] = useState(false);
  const [selectedItemUid, setSelectedItemUid] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEditRegion, setSelectedEditRegion] = useState("en");
  const [defaultRegion, setDefaultRegion] = useState(true);

  useEffect(() => {
    if (searchTerm.trim() !== "") {
      setOpenSection("pages");
    }
  }, [searchTerm]);

  const toggleSection = (section) => {
    setOpenSection((prev) => (prev === section ? null : section));
  };

  // NEW: track expanded nodes as a Set for O(1) checks
  const [expandedUids, setExpandedUids] = useState(() => new Set());

  const [draggedItem, setDraggedItem] = useState(null);
  const [draggedItemData, setDraggedItemData] = useState(null);
  const [overId, setOverId] = useState(null);
  const [rootDropPreviewIndex, setRootDropPreviewIndex] = useState(null);
  const [treeDropPreview, setTreeDropPreview] = useState(null);
  const [showIconModal, setShowIconModal] = useState(false);
  const [tempIcon, setTempIcon] = useState("");
  const [showMediaPicker, setShowMediaPicker] = useState(false);

  const [loading, setLoading] = useState(false);
  const [lastPointer, setLastPointer] = useState({ x: 0, y: 0 });
  const lastPointerRef = useRef({ x: 0, y: 0 });
  const lastPublishedPointerRef = useRef({ x: 0, y: 0 });
  const lastPublishedTimeRef = useRef(0);

  // Track which nodes we auto-collapsed during a drag so we can restore them after drag ends
  const [collapsedDuringDrag, setCollapsedDuringDrag] = useState(
    () => new Set(),
  );
  const pointerMoveRaf = useRef(null);
  const [navLabel, setNavLabel] = useState("");
  const [navUrl, setNavUrl] = useState("");

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 4,
      },
    }),
  );

  const handleAddPage = (page) => {
    const newItem = {
      page_id: page._id,
      name: { en: page.name },
      redirect_to: {
        en: page.slug || "",
      },
      children: [],
      extras: {},
      __uid: makeUID(),
      menu_id: null,
      target_blank: {
        en: false,
      },
      siblings: [
        {
          locale: "en",
          is_default: true,
          language: {
            name: "English",
          },
        },
      ],
    };

    const updated = [newItem, ...menuState];

    setMenuState(updated);
    formik.setValues(updated);
    setSelectedItemUid(newItem.__uid);
  };

  const handleAddButton = () => {
    const newItem = {
      name: { en: "Custom Button" },
      type: "primarybutton",
      redirect_to: {
        en: "",
      },
      children: [],
      extras: {},
      __uid: makeUID(),
      target_blank: {
        en: false,
      },
      siblings: [
        {
          locale: "en",
          is_default: true,
          language: {
            name: "English",
          },
        },
      ],
    };

    const updated = [newItem, ...menuState];

    setMenuState(updated);
    formik.setValues(updated);
    setSelectedItemUid(newItem.__uid);
  };

  // Get selected item
  const selectedItem = useMemo(() => {
    const find = (items) => {
      for (const item of items) {
        if (item.__uid === selectedItemUid) return item;
        if (item.children) {
          const found = find(item.children);
          if (found) return found;
        }
      }
      return null;
    };
    return find(menuState);
  }, [menuState, selectedItemUid]);

  useEffect(() => {
    if (selectedItem) {
      setNavLabel(selectedItem.name || "");
      setNavUrl(selectedItem.redirect_to || "");
      setNavIcon(selectedItem?.extras?.icon || "");
    }
  }, [selectedItem]);

  // Update handlers
  const updateSelectedItem = (field, value) => {
    const update = (items) =>
      items.map((item) => {
        if (item.__uid === selectedItemUid) {
          return { ...item, [field]: value };
        }

        if (item.children) {
          return { ...item, children: update(item.children) };
        }

        return item;
      });

    const updated = update(menuState);

    setMenuState(updated);
    formik.setValues(updated);
  };

  const updateMenuItemByUid = (uid, updater) => {
    const updateTree = (items) => {
      return items.map((item) => {
        if (item.__uid === uid) {
          return updater(item);
        }

        if (item.children) {
          return {
            ...item,
            children: updateTree(item.children),
          };
        }

        return item;
      });
    };

    setMenuState((prev) => updateTree(prev));
  };

  useEffect(() => {
    const onPointerMove = (e) => {
      if (pointerMoveRaf.current) return;
      pointerMoveRaf.current = window.requestAnimationFrame(() => {
        const next = { x: e.clientX, y: e.clientY };
        lastPointerRef.current = next;

        // Keep drag hover updates smooth by throttling pointer-driven state updates.
        // We still track exact pointer in ref for accurate drop placement.
        if (draggedItem) {
          const now = Date.now();
          const dx = Math.abs(next.x - lastPublishedPointerRef.current.x);
          const dy = Math.abs(next.y - lastPublishedPointerRef.current.y);
          const dt = now - lastPublishedTimeRef.current;

          // Combined throttle: require minimum movement OR minimum time passed if movement is small
          // But always limit frequency to avoiding thrashing loops
          if ((dx >= 8 || dy >= 8) && dt > 32) {
            setLastPointer(next);
            lastPublishedPointerRef.current = next;
            lastPublishedTimeRef.current = now;
          }
        }

        pointerMoveRaf.current = null;
      });
      // mouseCoords.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener("pointermove", onPointerMove);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      if (pointerMoveRaf.current) {
        window.cancelAnimationFrame(pointerMoveRaf.current);
        pointerMoveRaf.current = null;
      }
      // Cleanup auto-scroll interval on unmount
      // if (dragInterval.current) {
      //   clearInterval(dragInterval.current);
      // }
    };
  }, [draggedItem]);

  const formik = useFormik({
    initialValues: [],
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        // Remove __uid from menu items before sending
        const menuWithoutUids = removeUidsFromMenu(menuState);
        const payload = addMenuType(menuWithoutUids, selectedMenuType);

        const result = await requestWithToken(
          "PUT",
          `/api/menu/edit/all?menu_type=${selectedMenuType}`,
          payload,
        );

        if (result?.status === 200) {
          toast.success(result?.data?.message || "Navigation saved");
          fetchSelectedMenu(selectedMenuType);
        }
      } catch (error) {
        toast.error("Something went wrong", error.message);
      } finally {
        setLoading(false);
      }
    },
  });

  const usedPageIds = useMemo(
    () => collectAllMenuPageIds(menuState),
    [menuState],
  );

  const filteredPages = useMemo(() => {
    if (!searchTerm) return pageList.data?.list || [];

    return (pageList.data?.list || []).filter((page) =>
      page.name?.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [searchTerm, pageList.data?.list]);

  const getRootInsertIndexAtPointer = useCallback((items, pointerY) => {
    try {
      const container = document.querySelector('[data-menu-pages-root="true"]');
      if (!container) return items.length;

      // Filter out placeholder to avoid counting it as an item, preventing index oscillation
      const rootNodes = Array.from(
        container.querySelectorAll('[data-tree-depth="0"][data-menu-uid]'),
      ).filter(
        (node) => node.getAttribute("data-menu-uid") !== "__placeholder__",
      );

      if (rootNodes.length === 0) return 0;

      for (let i = 0; i < rootNodes.length; i++) {
        const rect = rootNodes[i].getBoundingClientRect();
        const midpoint = rect.top + rect.height / 2;
        if (pointerY <= midpoint) return i;
      }
    } catch {
      return items.length;
    }

    return items.length;
  }, []);

  const getTreeUidAtPoint = useCallback((x, y) => {
    try {
      const elements = document.elementsFromPoint(x, y);
      const node = elements.find((el) => {
        const uid = el?.getAttribute?.("data-menu-uid");
        return uid && uid !== "__placeholder__";
      });
      return node?.getAttribute?.("data-menu-uid") || null;
    } catch {
      return null;
    }
  }, []);

  const getClosestTreeUidAtPointerY = useCallback((y) => {
    try {
      const container = document.querySelector('[data-menu-pages-root="true"]');
      if (!container) return null;

      const nodes = Array.from(
        container.querySelectorAll("[data-menu-uid][data-tree-depth]"),
      ).filter(
        (node) => node.getAttribute("data-menu-uid") !== "__placeholder__",
      );

      if (!nodes.length) return null;

      let minTop = Number.POSITIVE_INFINITY;
      let maxBottom = Number.NEGATIVE_INFINITY;
      let bestUid = null;
      let bestDistance = Number.POSITIVE_INFINITY;

      for (const node of nodes) {
        const rect = node.getBoundingClientRect();

        if (rect.top < minTop) minTop = rect.top;
        if (rect.bottom > maxBottom) maxBottom = rect.bottom;

        if (y >= rect.top && y <= rect.bottom)
          return node.getAttribute("data-menu-uid");

        const distance = Math.min(
          Math.abs(y - rect.top),
          Math.abs(y - rect.bottom),
        );
        if (distance < bestDistance) {
          bestDistance = distance;
          bestUid = node.getAttribute("data-menu-uid");
        }
      }

      // If pointer is above first row or below last row, return null
      // so root drop index logic can place at start/end correctly.
      if (y < minTop || y > maxBottom) return null;

      return bestUid;
    } catch {
      return null;
    }
  }, []);

  const getTreeNodeRectByUid = useCallback((uid) => {
    try {
      const container = document.querySelector('[data-menu-pages-root="true"]');
      if (!container) return null;
      const el = container.querySelector(`[data-menu-uid="${uid}"]`);
      return el ? el.getBoundingClientRect() : null;
    } catch {
      return null;
    }
  }, []);

  const getAllowedDropMode = useCallback(
    (targetUid, requestedMode) => {
      if (requestedMode !== "child") return "before";
      const depth = findDepthByUid(menuState, targetUid);
      return depth <= 1 ? "child" : "before";
    },
    [menuState],
  );

  // Sync menuState when selectedMenuData changes
  useEffect(() => {
    if (selectedMenuData?.data?.menu) {
      const withUids = ensureUIDs(selectedMenuData.data.menu);
      setMenuState(withUids);
      formik.setValues(withUids);

      // Expand top-level items by default (optional).
      const initialExpanded = new Set(withUids.map((i) => i.__uid));
      setExpandedUids(initialExpanded);

      if (withUids.length > 0) {
        setSelectedItemUid(withUids[0].__uid);
      }
    }
  }, [selectedMenuData?.data?.menu]);

  /* ---------- Expand/collapse helpers ---------- */
  const toggleExpand = useCallback((uid) => {
    setExpandedUids((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    const all = new Set();
    const traverse = (arr) => {
      for (const item of arr) {
        all.add(item.__uid);
        if (item.children?.length) traverse(item.children);
      }
    };
    traverse(menuState);
    setExpandedUids(all);
  }, [menuState]);

  const collapseAll = useCallback(() => {
    setExpandedUids(new Set());
  }, []);

  /* ---------- Drag handlers (updated to collapse during drag) ---------- */

  const handleDragStart = useCallback(
    ({ active }) => {
      const currentPointer = lastPointerRef.current;
      lastPublishedPointerRef.current = currentPointer;
      setLastPointer(currentPointer);

      setDraggedItem(active.id);
      setRootDropPreviewIndex(null);
      setTreeDropPreview(null);
      // startAutoScroll(); // Start auto-scrolling

      if (active.id.startsWith("page-")) {
        const pageId = active.id.replace("page-", "");
        const page = pageList?.data?.list?.find((p) => p._id === pageId);
        setDraggedItemData(page?.name || "Item");
      } else if (active.id.startsWith("menu-")) {
        const uid = active.id.replace("menu-", "");
        const context = findItemWithContext(menuState, uid);
        setDraggedItemData(context?.item?.name || "Item");

        // AUTO-COLLAPSE: if the dragged item has children and is currently expanded, collapse it now
        if (context?.item?.children?.length && expandedUids.has(uid)) {
          setExpandedUids((prev) => {
            const next = new Set(prev);
            next.delete(uid);
            return next;
          });
          // remember we auto-collapsed this UID so we can restore it later
          setCollapsedDuringDrag((prev) => {
            const next = new Set(prev);
            next.add(uid);
            return next;
          });
        }
      }
    },
    [menuState, pageList, expandedUids],
  );

  const handleDragOver = useCallback(({ over }) => {
    setOverId(over?.id || null);
    if (!over || over.id !== "MENU_PAGES") {
      setRootDropPreviewIndex(null);
      setTreeDropPreview(null);
    }
  }, []);

  useEffect(() => {
    if (
      !draggedItem ||
      overId !== "MENU_PAGES" ||
      !draggedItem.startsWith("page-")
    ) {
      setRootDropPreviewIndex((prev) => (prev === null ? prev : null));
      setTreeDropPreview((prev) => (prev === null ? prev : null));
      return;
    }

    // Optimization: Check if we are hovering the placeholder we created.
    // If so, we are stable. Don't re-calculate to avoid layout thrashing.
    const elementCtx = document.elementFromPoint(lastPointer.x, lastPointer.y);
    if (elementCtx?.closest && elementCtx.closest(".menu-dnd-placeholder")) {
      return;
    }

    const pointerUid =
      getTreeUidAtPoint(lastPointer.x, lastPointer.y) ||
      getClosestTreeUidAtPointerY(lastPointer.y);

    if (pointerUid && findItemWithContext(menuState, pointerUid)) {
      const rect = getTreeNodeRectByUid(pointerUid);
      setTreeDropPreview((prev) => {
        const previousMode =
          prev?.targetUid === pointerUid ? prev.mode : "before";
        const nextMode = getAllowedDropMode(
          pointerUid,
          resolveDropModeByPointerX(rect, lastPointer.x, previousMode),
        );
        if (prev?.targetUid === pointerUid && prev?.mode === nextMode) {
          return prev;
        }
        return { targetUid: pointerUid, mode: nextMode };
      });
      setRootDropPreviewIndex((prev) => (prev === null ? prev : null));
      return;
    }

    const nextIndex = getRootInsertIndexAtPointer(menuState, lastPointer.y);
    setRootDropPreviewIndex((prev) => (prev === nextIndex ? prev : nextIndex));
    setTreeDropPreview((prev) => (prev === null ? prev : null));
  }, [
    draggedItem,
    overId,
    menuState,
    lastPointer.x,
    lastPointer.y,
    getRootInsertIndexAtPointer,
    getTreeUidAtPoint,
    getClosestTreeUidAtPointerY,
    getTreeNodeRectByUid,
    getAllowedDropMode,
  ]);

  const restoreCollapsedDuringDrag = useCallback(() => {
    if (!collapsedDuringDrag || collapsedDuringDrag.size === 0) return;
    setExpandedUids((prev) => {
      const next = new Set(prev);
      collapsedDuringDrag.forEach((uid) => next.add(uid));
      return next;
    });
    setCollapsedDuringDrag(new Set());
  }, [collapsedDuringDrag]);

  const handleDragEnd = useCallback(
    ({ active, over }) => {
      // Stop auto-scrolling
      // stopAutoScroll();

      // restore any nodes we auto-collapsed during drag
      restoreCollapsedDuringDrag();

      // Store the preview index before clearing it
      const dropIndex = rootDropPreviewIndex;

      setDraggedItem(null);
      setDraggedItemData(null);
      setOverId(null);
      setRootDropPreviewIndex(null);
      setTreeDropPreview(null);

      const activeData = active.data.current;
      if (!activeData) return;
      const isPageDrag = activeData.type === "page";

      const menuRootEl = document.querySelector(
        '[data-menu-pages-root="true"]',
      );
      const menuRootRect = menuRootEl?.getBoundingClientRect?.();
      const currentPointer = lastPointerRef.current;
      const pointerInsideMenuRoot =
        !!menuRootRect &&
        currentPointer.x >= menuRootRect.left &&
        currentPointer.x <= menuRootRect.right &&
        currentPointer.y >= menuRootRect.top &&
        currentPointer.y <= menuRootRect.bottom;

      if (!over && !(isPageDrag && pointerInsideMenuRoot)) return;
      const effectiveOverId = over?.id || "MENU_PAGES";

      // pointer coordinates from pointermove listener
      const pointerX = currentPointer.x;
      const pointerY = currentPointer.y;

      // Helper to get droppable element rect by target UID
      const getDroppableRect = (targetUid) => {
        try {
          const el = document.querySelector(
            `[data-droppable="droppable-${targetUid}"]`,
          );
          return el ? el.getBoundingClientRect() : null;
        } catch {
          return null;
        }
      };

      // deep clone current state to work with (we will build newMenuState from this)
      const currentMenuState = deepClone(menuState);

      // Track if we need to expand a parent after drop
      let parentUidToExpand = null;

      /* ---------- PAGE dropped (new item) ---------- */
      if (activeData.type === "page") {
        const page = activeData.page;
        if (!page) return;

        const existingIds = new Set(collectAllMenuPageIds(currentMenuState));
        if (existingIds.has(page._id)) return;

        const newItem = {
          page_id: page._id,
          name: page.name,
          redirect_to: {
            en: page.slug || "",
          },
          children: [],
          extras: {},
          __uid: makeUID(),
          menu_id: null,
          target_blank: {
            en: false,
          },
        };

        let newMenuState = currentMenuState;
        if (effectiveOverId === "MENU_PAGES") {
          const pointerTargetUid =
            treeDropPreview?.targetUid ||
            getTreeUidAtPoint(pointerX, pointerY) ||
            getClosestTreeUidAtPointerY(pointerY);
          const pointerMode = pointerTargetUid
            ? getAllowedDropMode(
                pointerTargetUid,
                treeDropPreview?.mode || "before",
              )
            : "before";
          if (
            pointerTargetUid &&
            findItemWithContext(currentMenuState, pointerTargetUid)
          ) {
            const rect = getTreeNodeRectByUid(pointerTargetUid);
            if (!rect) {
              if (pointerMode === "child") {
                newMenuState = addItemAsChild(
                  currentMenuState,
                  pointerTargetUid,
                  newItem,
                );
                parentUidToExpand = pointerTargetUid;
              } else {
                newMenuState = insertBeforeUid(
                  currentMenuState,
                  pointerTargetUid,
                  newItem,
                );
              }
            } else {
              if (pointerMode === "before") {
                newMenuState = insertBeforeUid(
                  currentMenuState,
                  pointerTargetUid,
                  newItem,
                );
              } else {
                newMenuState = addItemAsChild(
                  currentMenuState,
                  pointerTargetUid,
                  newItem,
                );
                parentUidToExpand = pointerTargetUid;
              }
            }
          } else {
            // Fallback to root-level insert
            const rootInsertIndex =
              dropIndex !== null
                ? dropIndex
                : getRootInsertIndexAtPointer(currentMenuState, pointerY);
            newMenuState = insertAtRootIndex(
              currentMenuState,
              rootInsertIndex,
              newItem,
            );
          }
        } else {
          const rawTargetId = String(effectiveOverId).startsWith(
            "droppable-menu-",
          )
            ? String(effectiveOverId).replace("droppable-menu-", "")
            : String(effectiveOverId).startsWith("menu-")
              ? String(effectiveOverId).replace("menu-", "")
              : String(effectiveOverId);
          const targetDroppableId = findUidByAnyId(
            currentMenuState,
            rawTargetId,
          );

          if (!targetDroppableId) {
            // If we couldn't resolve an ID, and items exist, don't fall back to root add.
            if (currentMenuState.length > 0) return;
            newMenuState = [...currentMenuState, newItem];
          } else {
            const rect = getDroppableRect(targetDroppableId);
            // If we can't find rect (maybe because DataSortableTree hides internals?),
            // fallback to append as child if ID is valid
            if (!rect) {
              const allowedMode = getAllowedDropMode(
                targetDroppableId,
                "child",
              );
              if (allowedMode === "child") {
                newMenuState = addItemAsChild(
                  currentMenuState,
                  targetDroppableId,
                  newItem,
                );
                parentUidToExpand = targetDroppableId;
              } else {
                newMenuState = insertBeforeUid(
                  currentMenuState,
                  targetDroppableId,
                  newItem,
                );
              }
            } else {
              const requestedMode = resolveDropModeByPointerX(
                rect,
                pointerX,
                "before",
              );
              const allowedMode = getAllowedDropMode(
                targetDroppableId,
                requestedMode,
              );
              if (allowedMode === "before") {
                newMenuState = insertBeforeUid(
                  currentMenuState,
                  targetDroppableId,
                  newItem,
                );
              } else {
                newMenuState = addItemAsChild(
                  currentMenuState,
                  targetDroppableId,
                  newItem,
                );
                parentUidToExpand = targetDroppableId;
              }
            }
          }
        }

        setMenuState(newMenuState);
        formik.setValues(newMenuState);
      }

      /* ---------- MENU item moved ---------- */
      if (activeData.type === "item") {
        const sourceUid = activeData.item?.__uid;
        if (!sourceUid) return;

        const sourceContext = findItemWithContext(currentMenuState, sourceUid);
        if (!sourceContext) {
          return;
        }

        const sourceNodeFresh = deepClone(sourceContext.item);

        const itemsWithoutSource = removeItemByUid(currentMenuState, sourceUid);

        let newMenuState = itemsWithoutSource;

        if (effectiveOverId === "MENU_PAGES") {
          const itemWithNullMenuId = {
            ...sourceNodeFresh,
            menu_id: null,
            children: sourceNodeFresh.children
              ? sourceNodeFresh.children.map((child) => ({ ...child }))
              : [],
          };
          // Use the stored dropIndex instead of recalculating to ensure exact placement
          const rootInsertIndex =
            dropIndex !== null
              ? dropIndex
              : getRootInsertIndexAtPointer(itemsWithoutSource, pointerY);
          newMenuState = insertAtRootIndex(
            itemsWithoutSource,
            rootInsertIndex,
            itemWithNullMenuId,
          );
        } else {
          const rawTargetId = String(effectiveOverId).startsWith(
            "droppable-menu-",
          )
            ? String(effectiveOverId).replace("droppable-menu-", "")
            : String(effectiveOverId).startsWith("menu-")
              ? String(effectiveOverId).replace("menu-", "")
              : String(effectiveOverId);
          const targetDroppableId = findUidByAnyId(
            currentMenuState,
            rawTargetId,
          );

          if (!targetDroppableId) {
            const itemWithNullMenuId = {
              ...sourceNodeFresh,
              menu_id: null,
            };
            newMenuState = [...itemsWithoutSource, itemWithNullMenuId];
          } else {
            if (targetDroppableId === sourceUid) {
              setMenuState(itemsWithoutSource);
              formik.setValues(itemsWithoutSource);
              return;
            }

            const isDescendant = (parent, childUid) => {
              if (!parent.children) return false;
              for (const child of parent.children) {
                if (child.__uid === childUid) return true;
                if (isDescendant(child, childUid)) return true;
              }
              return false;
            };

            if (isDescendant(sourceNodeFresh, targetDroppableId)) {
              setMenuState(itemsWithoutSource);
              formik.setValues(itemsWithoutSource);
              return;
            }

            const rect = getDroppableRect(targetDroppableId);
            if (!rect) {
              const allowedMode = getAllowedDropMode(
                targetDroppableId,
                "child",
              );
              if (allowedMode === "child") {
                newMenuState = addItemAsChild(
                  itemsWithoutSource,
                  targetDroppableId,
                  sourceNodeFresh,
                );
                parentUidToExpand = targetDroppableId;
              } else {
                const itemForInsertion = {
                  ...sourceNodeFresh,
                  menu_id: null,
                };
                newMenuState = insertBeforeUid(
                  itemsWithoutSource,
                  targetDroppableId,
                  itemForInsertion,
                );
              }
            } else {
              const requestedMode = resolveDropModeByPointerX(
                rect,
                pointerX,
                "before",
              );
              const allowedMode = getAllowedDropMode(
                targetDroppableId,
                requestedMode,
              );
              if (allowedMode === "before") {
                const itemForInsertion = {
                  ...sourceNodeFresh,
                  menu_id: null,
                };
                newMenuState = insertBeforeUid(
                  itemsWithoutSource,
                  targetDroppableId,
                  itemForInsertion,
                );
              } else {
                newMenuState = addItemAsChild(
                  itemsWithoutSource,
                  targetDroppableId,
                  sourceNodeFresh,
                );
                parentUidToExpand = targetDroppableId;
              }
            }
          }
        }

        setMenuState(newMenuState);
        formik.setValues(newMenuState);
      }

      if (parentUidToExpand && !expandedUids.has(parentUidToExpand)) {
        setExpandedUids((prev) => new Set(prev).add(parentUidToExpand));
      }
    },
    [
      menuState,
      getRootInsertIndexAtPointer,
      getTreeUidAtPoint,
      getClosestTreeUidAtPointerY,
      getTreeNodeRectByUid,
      getAllowedDropMode,
      restoreCollapsedDuringDrag,
      expandedUids,
      formik,
      rootDropPreviewIndex,
      treeDropPreview,
      // stopAutoScroll,
    ],
  );

  // called when a drag is cancelled (user presses ESC or similar)
  const handleDragCancel = useCallback(() => {
    // stopAutoScroll();
    restoreCollapsedDuringDrag();
    setDraggedItem(null);
    setDraggedItemData(null);
    setOverId(null);
    setRootDropPreviewIndex(null);
    setTreeDropPreview(null);
  }, [restoreCollapsedDuringDrag]);

  const handleEdit = useCallback((item) => {
    setEditMenuItem({ item, path: [] });
    setActiveModal("editMenuItem");
  }, []);

  function removeItemById(items, idToRemove) {
    return items
      .filter((item) =>
        item._id
          ? item._id !== idToRemove
          : item.page_id
            ? item.page_id !== idToRemove
            : item.id !== idToRemove,
      )
      .map((item) => ({
        ...item,
        children: item.children?.length
          ? removeItemById(item.children, idToRemove)
          : [],
      }));
  }

  const handleAddCustomLink = () => {
    const newItem = {
      id: String(Date.now() + Math.random()),
      name: {
        en: "Custom Link",
      },
      redirect_to: {
        en: "",
      },
      children: [],
      extras: {},
      __uid: makeUID(),
      menu_id: null,
      target_blank: {
        en: false,
      },
      type: "customlinks",
      siblings: [
        {
          locale: "en",
          is_default: true,
          language: {
            name: "English",
          },
        },
      ],
    };

    const updated = [newItem, ...menuState];

    setMenuState(updated);
    formik.setValues(updated);
    setSelectedItemUid(newItem.__uid);
  };

  const handleDeleteSelected = () => {
    if (!selectedItemUid) return;

    const item = selectedItem;

    const updated = removeItemByUid(menuState, selectedItemUid);

    setMenuState(updated);
    formik.setValues(updated);

    if (updated.length > 0) {
      setSelectedItemUid(updated[0].__uid);
    } else {
      setSelectedItemUid(null);
    }
  };

  const handleMenuItemModalSave = (modalVals) => {
    let newMenus;
    if (editMenuItem.item) {
      const updateItemInTree = (items, targetUid, updates) => {
        return items.map((item) => {
          if (item.__uid === targetUid) {
            return { ...item, ...updates };
          }
          if (item.children) {
            return {
              ...item,
              children: updateItemInTree(item.children, targetUid, updates),
            };
          }
          return item;
        });
      };

      newMenus = updateItemInTree(menuState, editMenuItem.item.__uid, {
        name: modalVals.name,
        redirect_to: modalVals.redirect_to,
        extras: {
          icon: modalVals.extras?.icon || "",
          desc: modalVals.extras?.desc || "",
        },
      });
    } else {
      const newItem = {
        id: String(Date.now() + Math.random()),
        name: modalVals.name,
        redirect_to: modalVals.redirect_to,
        children: [],
        extras: {
          icon: modalVals.extras?.icon || "",
          desc: modalVals.extras?.desc || "",
        },
        __uid: makeUID(),
        menu_id: null,
        target_blank: {
          en: false,
        },
      };
      newMenus = [...menuState, newItem];
    }

    setMenuState(newMenus);
    formik.setValues(newMenus);
    setActiveModal(null);
    setEditMenuItem({ item: null, path: [] });
  };

  /* ---------------- Data fetching ---------------- */
  const fetchMenuTypes = async () => {
    try {
      setMenuTypes((v) => ({ ...v, loading: true }));
      const result = await requestWithToken("GET", "/api/menutype");
      if (result?.status === 200) {
        setMenuTypes({ loading: false, data: result.data.data });
        setSelectedMenuType(result.data.data[0]?.slug || null);
      }
    } catch {
      setMenuTypes((v) => ({ ...v, loading: false, data: [] }));
    }
  };

  const fetchSelectedMenu = async (slug) => {
    try {
      setSelectedMenuData((v) => ({ ...v, loading: true }));
      const result = await requestWithToken(
        "GET",
        `/api/menu/all/navigations?menu_type_slug=${slug}`,
      );
      if (result?.status === 200) {
        setSelectedMenuData({ loading: false, data: result.data.data });

        const withUids = ensureUIDs(result.data.data || []);

        setMenuState(withUids);
        formik.setValues(withUids);
        if (withUids.length > 0) {
          setSelectedItemUid(withUids[0].__uid);
        } else {
          setSelectedItemUid(null);
        }
      }
    } catch {
      setSelectedMenuData((v) => ({ ...v, loading: false, data: null }));
      setMenuState([]);
      formik.setValues([]);
    }
  };

  const fetchPageList = async () => {
    try {
      setPageList((v) => ({ ...v, loading: true }));
      const result = await requestWithToken(
        "GET",
        "/api/page/navigation/menu/list?page=1&limit=100",
      );
      if (result?.status === 200) {
        const menudata = result.data.data?.list.map((page) => ({
          ...page,
          name: page.title || "",
        }));
        setPageList({
          loading: false,
          data: { ...result.data.data, list: menudata },
        });
      }
    } catch {
      setPageList((v) => ({ ...v, loading: false, data: [] }));
    }
  };

  useEffect(() => {
    fetchMenuTypes();
    fetchPageList();
  }, []);

  useEffect(() => {
    if (selectedMenuType) fetchSelectedMenu(selectedMenuType);
  }, [selectedMenuType]);

  return (
    <>
      <PageHeader pageTitle="Navigation">
        <Help
          title={helpPagesData.pages?.title || helpPagesData.menu?.title}
          message={helpPagesData.pages?.message || helpPagesData.menu?.message}
          items={helpPagesData.pages?.items || helpPagesData.menu?.items}
        />
        <button
          className="btn-black"
          type="submit"
          onClick={async () => {
            // formik.setValues(menuState);
            // formik.handleSubmit();

            await formik.setValues(menuState);

            // Run validation manually
            const errors = await formik.validateForm();

            if (Object.keys(errors).length > 0) {
              const firstError = getFirstError(errors);

              toast.error(firstError || "Validation error");
              return;
            }

            // If valid → submit
            formik.handleSubmit();
          }}
          disabled={loading}
        >
          {loading ? <Spinner size="sm" /> : "Save Navigation"}
        </button>
      </PageHeader>

      <div className="page-container navigation">
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
          collisionDetection={customCollisionDetection}
        >
          <div className="nav-columns">
            <div className="nav-col col1">
              <div className="theme-card">
                <div className="sticky-sec">
                  <div className="p-3">
                    <div className="card-title mb-3">All Pages</div>

                    {/* Search */}
                    <div className="mb-3">
                      <div className="search-input frm-input">
                        <input
                          type="text"
                          className=""
                          value={searchTerm}
                          placeholder="Search Pages"
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <img src={searchicon} alt="icon" />
                      </div>
                    </div>

                    {/* Pages dropdown */}
                    <div className="mb-3">
                      <div
                        className="item-label d-flex justify-content-between pointer"
                        onClick={() => toggleSection("pages")}
                      >
                        PAGES
                        <img
                          src={arrowdown}
                          alt="icon"
                          style={{
                            transform:
                              openSection === "pages"
                                ? "rotate(0deg)"
                                : "rotate(180deg)",
                            transition: "0.2s",
                          }}
                        />
                      </div>
                      {openSection === "pages" && (
                        <>
                          {pageList.loading ? (
                            <Spinner size="sm" />
                          ) : filteredPages.length ? (
                            <div>
                              {filteredPages.length === 0 ? (
                                <div>No pages found</div>
                              ) : (
                                filteredPages.map((page) => (
                                  <div
                                    key={page._id}
                                    onClick={() => handleAddPage(page)}
                                    className="d-flex align-items-center pointer justify-content-between page-item"
                                  >
                                    <span>{page.name}</span>

                                    <img
                                      src={addIcon}
                                      width={16}
                                      className="pointer"
                                    />
                                  </div>
                                ))
                              )}
                            </div>
                          ) : (
                            <div>No pages available</div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Components */}
                    <div>
                      <div
                        className="item-label d-flex justify-content-between pointer"
                        onClick={() => toggleSection("components")}
                      >
                        COMPONENT
                        <img
                          src={arrowdown}
                          alt="icon"
                          style={{
                            transform:
                              openSection === "components"
                                ? "rotate(0deg)"
                                : "rotate(180deg)",
                            transition: "0.2s",
                          }}
                        />
                      </div>

                      {openSection === "components" && (
                        <>
                          <div
                            className="d-flex justify-content-between align-items-center page-item pointer"
                            onClick={() => handleAddButton()}
                          >
                            Custom Button
                            <img src={addIcon} width={16} />
                          </div>

                          <div
                            className="d-flex justify-content-between align-items-center page-item pointer"
                            onClick={handleAddCustomLink}
                          >
                            Custom URL
                            <img src={addIcon} width={16} />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="nav-col col2">
              <div className="theme-card">
                <div className="sticky-sec">
                  <div className="p-3 d-flex align-items-center">
                    <div className="card-title">Select Menu to Edit</div>

                    <div className="sel-box ms-auto">
                      <select
                        className="theme-select pointer"
                        onChange={(e) => setSelectedMenuType(e.target.value)}
                        value={selectedMenuType || ""}
                      >
                        {menuTypes.loading ? (
                          <option>Loading...</option>
                        ) : menuTypes.data.length ? (
                          menuTypes.data.map((menu) => (
                            <option key={menu._id} value={menu.slug}>
                              {menu.name}
                            </option>
                          ))
                        ) : (
                          <option>No Menu Types</option>
                        )}
                      </select>
                    </div>
                  </div>
                  <div className="p-3">
                    <DroppableMenuPages
                      isOver={overId === "MENU_PAGES"}
                      hasItems={menuState.length > 0}
                    >
                      {menuState.length > 0 ? (
                        <DataSortableTree
                          allIteams={menuState}
                          selectedItemUid={selectedItemUid}
                          onSelectItem={(item) =>
                            setSelectedItemUid(item.__uid)
                          }
                          externalDropPreviewIndex={rootDropPreviewIndex}
                          externalTreeDropPreview={treeDropPreview}
                          isDragging={
                            draggedItem !== null && overId === "MENU_PAGES"
                          }
                          setAllItems={(values) => {
                            setMenuState(values);
                            formik.setValues(values);
                          }}
                          onRemoveItem={(id) => {
                            setMenuState(removeItemById(menuState, id));
                          }}
                        />
                      ) : (
                        <div className="text-center py-5 text-muted">
                          Drag pages here to build your menu
                        </div>
                      )}
                    </DroppableMenuPages>
                  </div>
                </div>
              </div>
            </div>
            {menuState.length > 0 && (
              <div className="nav-col col3">
                <div className="theme-card">
                  <div className="p-3 sticky-sec">
                    <div className="card-title mb-4 d_flex">
                      Edit Item
                      <span
                        className="delete-txt"
                        onClick={handleDeleteSelected}
                      >
                        Delete
                      </span>
                    </div>
                    <NavigationRegionSpecific
                      siblings={selectedItem?.siblings || []}
                      onEditRegion={(locale, is_default) => {
                        setSelectedEditRegion(locale);
                        setDefaultRegion(is_default);
                      }}
                      onAddRegion={(newRegion) => {
                        const locale = newRegion.locale;
                        const addLanguageToAllItems = (items) => {
                          return items.map((item) => {
                            const defaultName = item.name?.en || Object.values(item.name || {})[0] || "";
                            const defaultRedirect = item.redirect_to?.en || Object.values(item.redirect_to || {})[0] || "";
                            const defaultDesc = item.extras?.desc?.en || Object.values(item.extras?.desc || {})[0] || "";
                            const defaultTargetBlank = typeof item.target_blank === "object" 
                              ? (item.target_blank?.en || Object.values(item.target_blank || {})[0] || false)
                              : (item.target_blank ?? false);

                            const updatedItem = {
                              ...item,
                              siblings: [...(item.siblings || []).filter(s => s.locale !== locale), newRegion],
                              name: {
                                ...(item.name && typeof item.name === "object" ? item.name : {}),
                                [locale]: defaultName,
                              },
                              redirect_to: {
                                ...(item.redirect_to && typeof item.redirect_to === "object" ? item.redirect_to : {}),
                                [locale]: defaultRedirect,
                              },
                              target_blank: {
                                ...(item.target_blank && typeof item.target_blank === "object" ? item.target_blank : {}),
                                [locale]: defaultTargetBlank,
                              },
                              extras: {
                                ...(item.extras || {}),
                                desc: {
                                  ...(item.extras?.desc || {}),
                                  [locale]: defaultDesc,
                                },
                              },
                            };

                            if (item.children && item.children.length > 0) {
                              updatedItem.children = addLanguageToAllItems(item.children);
                            }

                            return updatedItem;
                          });
                        };

                        setMenuState((prev) => addLanguageToAllItems(prev));
                      }}
                      onDeleteRegion={(locale) => {
                        const deleteLanguageFromAllItems = (items) => {
                          return items.map((item) => {
                            const updatedSiblings = (item.siblings || []).filter(
                              (s) => s.locale !== locale || s.is_default === true
                            );
                            const updatedName = { ...(item.name || {}) };
                            delete updatedName[locale];
                            
                            const updatedRedirect = { ...(item.redirect_to || {}) };
                            delete updatedRedirect[locale];

                            const updatedTargetBlank = typeof item.target_blank === "object"
                              ? { ...item.target_blank }
                              : {};
                            if (typeof item.target_blank === "object") {
                              delete updatedTargetBlank[locale];
                            }

                            const updatedDesc = { ...(item.extras?.desc || {}) };
                            delete updatedDesc[locale];

                            const updatedItem = {
                              ...item,
                              siblings: updatedSiblings,
                              name: updatedName,
                              redirect_to: updatedRedirect,
                              target_blank: typeof item.target_blank === "object" ? updatedTargetBlank : item.target_blank,
                              extras: {
                                ...(item.extras || {}),
                                desc: updatedDesc,
                              },
                            };

                            if (item.children && item.children.length > 0) {
                              updatedItem.children = deleteLanguageFromAllItems(item.children);
                            }

                            return updatedItem;
                          });
                        };

                        setMenuState((prev) => deleteLanguageFromAllItems(prev));

                        // 👉 Optional: switch back to default region after delete
                        setSelectedEditRegion("en");
                      }}
                    />

                    <div className="frm-group">
                      <label className="frm-label d-flex align-items-center">
                        Navigation Label
                        <img
                          className="ms-2"
                          src={infoCircle}
                          alt="Info"
                          title="Add Navigation Label"
                        />
                      </label>
                      <input
                        type="text"
                        className="frm-input"
                        placeholder="Navigation label"
                        value={
                          typeof selectedItem?.name === "object"
                            ? selectedItem?.name?.[selectedEditRegion] || ""
                            : selectedItem?.name || ""
                        }
                        onChange={(e) => {
                          const value = e.target.value;

                          updateMenuItemByUid(selectedItemUid, (item) => ({
                            ...item,
                            name: {
                              ...(item.name || {}),
                              [selectedEditRegion]: value,
                            },
                          }));
                        }}
                      />
                    </div>

                    <div className="frm-group ">
                      <label className="frm-label d-flex align-items-center">
                        URL
                        <img
                          className="ms-2"
                          src={infoCircle}
                          alt="Info"
                          title="Enter URL"
                        />
                      </label>
                      <input
                        type="text"
                        className="frm-input"
                        placeholder="https://example.com or /slug"
                        // value={selectedItem?.redirect_to || ""}
                        // onChange={(e) =>
                        //   updateSelectedItem("redirect_to", e.target.value)
                        // }
                        value={
                          typeof selectedItem?.redirect_to === "object"
                            ? selectedItem?.redirect_to?.[selectedEditRegion] ||
                              ""
                            : selectedItem?.redirect_to || ""
                        }
                        onChange={(e) => {
                          const value = e.target.value;

                          updateMenuItemByUid(selectedItemUid, (item) => ({
                            ...item,
                            redirect_to: {
                              ...(item.redirect_to || {}),
                              [selectedEditRegion]: value,
                            },
                          }));
                        }}
                        // disabled={!defaultRegion}
                      />
                    </div>

                    <div className="frm-group pb-4 br-btm">
                      <label className="frm-label d-flex align-items-center">
                        Description
                        <img
                          className="ms-2"
                          src={infoCircle}
                          alt="Info"
                          title="Enter Description"
                        />
                      </label>
                      <input
                        type="text"
                        className="frm-input"
                        placeholder="Enter menu description"
                        value={
                          typeof selectedItem?.extras?.desc === "object"
                            ? selectedItem?.extras?.desc?.[
                                selectedEditRegion
                              ] || ""
                            : selectedItem?.extras?.desc || ""
                        }
                        onChange={(e) => {
                          const value = e.target.value;

                          updateMenuItemByUid(selectedItemUid, (item) => ({
                            ...item, // ✅ keep other fields

                            extras: {
                              ...(item.extras || {}), // ✅ keep existing extras

                              desc: {
                                ...(item.extras?.desc || {}), // ✅ keep existing desc values
                                [selectedEditRegion]: value, // ✅ dynamic key
                              },
                            },
                          }));
                        }}
                      />
                    </div>
                    {/* Visuals */}
                    <div className="frm-group">
                      <label className="frm-label">Visuals</label>

                      {/* Icon */}
                      <div className="frm-group">
                        <label className="frm-label">Icon</label>

                        <div
                          className="frm-input icon-field upload d-flex align-items-center justify-content-between pointer"
                          onClick={() => {
                            if (defaultRegion) {
                              setShowIconModal(true);
                            }
                          }}
                        >
                          {selectedItem?.extras?.icon ? (
                            <img
                              src={bucketUrl + selectedItem.extras.icon}
                              height={24}
                            />
                          ) : (
                            <span>Select Icon</span>
                          )}

                          <span className="upload-icons">
                            <img src={gallary} alt="icon" />
                          </span>
                        </div>
                      </div>

                      {/* Open in new tab */}
                      <div className="d-flex align-items-center justify-content-between mt-3">
                        <label className="frm-label mb-0">
                          Open in new tab
                        </label>
                        <SwitchToggle
                          checked={
                            typeof selectedItem?.target_blank === "object"
                              ? selectedItem?.target_blank?.[
                                  selectedEditRegion
                                ] || ""
                              : selectedItem?.target_blank || ""
                          }
                          // defaultRegion={defaultRegion}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            // updateSelectedItem("target_blank", isChecked);
                            updateMenuItemByUid(selectedItemUid, (item) => ({
                              ...item,
                              target_blank: {
                                ...(item.target_blank || {}),
                                [selectedEditRegion]: isChecked,
                              },
                            }));
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DragOverlay>
            {draggedItem && draggedItemData ? (
              <div
                style={{
                  padding: "12px 16px",
                  backgroundColor: "#0ea5e9",
                  color: "white",
                  borderRadius: "8px",
                  boxShadow:
                    "0 12px 24px rgba(14, 165, 233, 0.4), 0 4px 8px rgba(0, 0, 0, 0.1)",
                  fontSize: "14px",
                  fontWeight: "600",
                  whiteSpace: "nowrap",
                  maxWidth: "320px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  border: "2px solid rgba(255, 255, 255, 0.3)",
                  cursor: "grabbing",
                  // transform: "rotate(-2deg)",
                  animation: "pulse 2s ease-in-out infinite",
                }}
              >
                {draggedItemData}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
      <CommonModal show={showIconModal} onClose={() => setShowIconModal(false)}>
        <h5>Select Icon</h5>

        <div
          className="upload-box pointer"
          onClick={() => setShowMediaPicker(true)}
        >
          {!tempIcon ? (
            <>
              <img src={uploadIcon} alt="icon" />
              <span>Drag and drop or click to browse</span>
            </>
          ) : (
            <img src={bucketUrl + tempIcon} height={60} />
          )}
        </div>

        <div className="modal-actions addicon-actions">
          <button
            onClick={() => {
              setTempIcon("");
              setShowIconModal(false);
            }}
            className="white-btn"
          >
            Cancel
          </button>

          {!tempIcon ? (
            <button
              className="black-btn"
              onClick={() => setShowMediaPicker(true)}
            >
              Browse
            </button>
          ) : (
            <button
              className="black-btn"
              onClick={() => {
                updateSelectedItem("extras", {
                  ...(selectedItem?.extras || {}),
                  icon: tempIcon,
                });
                setShowIconModal(false);
              }}
            >
              Apply
            </button>
          )}
        </div>

        <MediaPickerModal
          show={showMediaPicker}
          onClose={() => setShowMediaPicker(false)}
          onSelect={(url) => {
            const index = url.indexOf("media/");
            const shortPath = index !== -1 ? url.substring(index) : url;

            setTempIcon(shortPath);
            setShowMediaPicker(false);
          }}
        />
      </CommonModal>
    </>
  );
}
